import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { TrackingScreenNamesEnum } from '../../../tracking/tracking.data';
import {
  I2gNgTrackingActionBuilder,
  I2gNgTrackingService,
  ITrackingAction,
  ITrackingObject,
  ITrackingProperties,
} from 'i2g-ng-tracking';
import { BehaviorSubject, Observable, Subject, timer } from 'rxjs';
import { Challenge } from '../../store/models/sub-models';
import { AccountStore } from '../../store/stores';
import { AccountDispatcher } from '../../store/dispatchers';
import {
  delay,
  filter,
  flatMap,
  map,
  shareReplay,
  skip,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';
import { LocalizationStore } from '../../../localization/store/stores';
import { URLS } from 'i2g-ng-utils';
import { IconSizesEnum, IIcon } from 'i2g-ng-ui';

@Component({
  selector: 'app-unusual-activity',
  templateUrl: './unusual-activity.component.html',
  styleUrls: ['./unusual-activity.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnusualActivityComponent
  implements OnInit, AfterViewInit, OnDestroy {
  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _tabVisible$: BehaviorSubject<boolean>;

  contactUsUrl: string;
  validatePromptIcon: IIcon;
  backIcon: IIcon;

  trackingScreenName: TrackingScreenNamesEnum;
  resendEmailAction: ITrackingAction;
  contactUsAction: ITrackingAction;
  trackingAdditionalProperties$: Observable<ITrackingProperties>;

  error$: Observable<string>;
  challenge$: Observable<Challenge>;
  trackingObject$: Observable<ITrackingObject>;
  emailResent$: BehaviorSubject<boolean>;

  constructor(
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _accountDispatcher: AccountDispatcher,
    private _accountStore: AccountStore,
    private _localizationStore: LocalizationStore,
    private _trackingService: I2gNgTrackingService,
    @Inject(DOCUMENT) private _document: Document
  ) {}

  ngOnInit() {
    this._tabVisible$ = new BehaviorSubject<boolean>(true);

    this.contactUsUrl = URLS.SUPPORT_REQUESTS;
    this.trackingScreenName =
      TrackingScreenNamesEnum.unusual_login_verification;
    this.resendEmailAction = this._trackingActionBuilder.buildButtonTapped(
      'resend_email',
      this.trackingScreenName
    );
    this.contactUsAction = this._trackingActionBuilder.buildButtonTapped(
      'contact_us',
      this.trackingScreenName
    );
    this.validatePromptIcon = {
      name: 'validate-prompt',
      size: IconSizesEnum.custom,
    };
    this.backIcon = {
      name: 'chevron-left',
      size: IconSizesEnum.x_small,
    };
    this.emailResent$ = new BehaviorSubject<boolean>(false);

    this.challenge$ = this._accountStore.getChallenge().pipe(shareReplay(1));

    this.error$ = this._accountStore.getUnusualLoginError().pipe(
      filter((x) => !!x),
      map((error) => error.description)
    );

    this.trackingObject$ = this._accountStore.getTrackingObject().pipe(
      filter((x) => !!x),
      shareReplay(1)
    );

    this.trackingAdditionalProperties$ = this.challenge$.pipe(
      map((challenge) => ({ reason: challenge.reason })),
      shareReplay(1)
    );

    // retry login on tab visible
    this._document.addEventListener('visibilitychange', () =>
      this._tabVisible$.next(!this._document.hidden)
    );

    // auto retry after every 5 seconds if document is visible
    timer(5000, 5000)
      .pipe(takeUntil(this._destroyed))
      .subscribe(() => this._tabVisible$.next(!this._document.hidden));

    this._tabVisible$
      .pipe(
        filter((x) => !!x),
        skip(1),
        delay(500),
        flatMap(() => this.challenge$),
        filter((x) => !!x),
        withLatestFrom(this._localizationStore.getCurrentLocale()),
        takeUntil(this._destroyed)
      )
      .subscribe(([challenge, locale]: [Challenge, string]) =>
        this._accountDispatcher.loginAccountByChallenge(
          challenge,
          locale,
          this.trackingScreenName
        )
      );
  }

  resendEmail(attemptId: string) {
    this.emailResent$.next(true);

    this._accountDispatcher.resendUnusualLoginEmail(attemptId);
  }

  ngAfterViewInit() {
    this._trackingService.trackPage(this.trackingScreenName);
  }

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
